import { useEffect, useRef, useState } from "react";
import { useForm } from "@inertiajs/react";
import { color, radius, type } from "@/lib/design-tokens";
import { contact as copy } from "@/lib/copy/contact";

/**
 * The one form on the site.
 *
 * It posts to `/contact` — the same endpoint the previous design used, with the
 * same field names and the same honeypot — because that controller, its
 * validations and its mailer are working and tested. Only the presentation is
 * new. If you change a field name here, change it in ContactController too, and
 * expect test/controllers/contact_controller_test.rb to tell you.
 *
 * **This form cannot confirm an appointment**, and nothing in it may imply one.
 * Cal.com is the live scheduler; this separate contact path delivers a message
 * to a person. The confirmation copy says exactly that and makes no promise
 * about turnaround, because nobody has measured one.
 */
export function BluebirdContactForm({
  presetTopic,
  initialEmail = "",
  returnTo,
}: {
  presetTopic?: string;
  initialEmail?: string;
  returnTo?: string;
}) {
  const [sent, setSent] = useState(false);
  const heading = useRef<HTMLHeadingElement | null>(null);
  const selectedTopic =
    presetTopic && copy.topics.some((topic) => topic.value === presetTopic)
      ? presetTopic
      : "";

  // Move focus to the confirmation when it appears. `role="status"` announces
  // it, but it does not move focus, which strands a keyboard user on a submit
  // button that no longer exists.
  useEffect(() => {
    if (sent) heading.current?.focus();
  }, [sent]);

  const { data, setData, post, processing, errors, reset } = useForm({
    firstName: "",
    lastName: "",
    email: initialEmail ?? "",
    phone: "",
    inquiryType: selectedTopic,
    message: selectedTopic === "newsletter" ? copy.newsletterRequest : "",
    is_robot: "",
    source: returnTo ? "contact-widget" : "",
    return_to: returnTo || "",
  });

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Captured BEFORE reset(), which clears data.inquiryType synchronously —
    // reading it afterwards sends an empty analytics payload.
    const inquiryType = data.inquiryType;
    post("/contact", {
      preserveScroll: true,
      onSuccess: () => {
        // UI first, analytics second. If gtag is missing, blocked, or has been
        // overwritten by a browser extension, the person still sees their
        // message land. `typeof gtag === "function"` rather than
        // `!== "undefined"` because some extensions replace it with null, which
        // passes the looser check and then throws on call.
        reset();
        setSent(true);
        if (typeof gtag === "function") {
          try {
            gtag("event", "contact_submit", { inquiry_type: inquiryType });
          } catch {
            /* analytics is best-effort, never a gate on the conversion */
          }
        }
      },
    });
  };

  if (sent) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          background: color.white,
          border: `1px solid ${color.borderCard}`,
          borderRadius: radius.panel,
          padding: "clamp(24px,3vw,36px)",
        }}
      >
        <h2
          ref={heading}
          tabIndex={-1}
          style={{ margin: 0, fontWeight: 800, fontSize: type.h3, letterSpacing: "-.01em", outline: "none" }}
        >
          {copy.sentHeading}
        </h2>
        <p style={{ margin: "12px 0 0", fontSize: "16px", lineHeight: 1.55, color: color.inkMuted }}>
          {copy.sentBody}
        </p>
      </div>
    );
  }

  // `.bb-field` makes these border-box. See app/frontend/styles/bluebird.css —
  // without it a 100%-wide field plus padding overflows a 390px viewport.
  const fieldClass = "bb-field";
  const field: React.CSSProperties = {
    width: "100%",
    fontFamily: "inherit",
    fontSize: "16px",
    color: color.ink,
    background: color.white,
    border: `1.5px solid ${color.borderCard}`,
    borderRadius: radius.button,
    padding: "13px 14px",
    outline: "none",
  };
  const label: React.CSSProperties = {
    display: "block",
    fontSize: "14px",
    fontWeight: 700,
    marginBottom: "6px",
  };
  const errorLine: React.CSSProperties = {
    marginTop: "6px",
    fontSize: "14px",
    color: color.soldRed,
  };

  return (
    <form onSubmit={submit} noValidate style={{ display: "grid", gap: "18px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(200px,100%),1fr))", gap: "18px" }}>
        <div>
          <label htmlFor="firstName" style={label}>
            {copy.fields.firstName}
          </label>
          <input
            id="firstName"
            name="firstName"
            className={fieldClass}
            style={field}
            value={data.firstName}
            onChange={(e) => setData("firstName", e.target.value)}
            autoComplete="given-name"
            required
          />
          {errors.firstName && <div style={errorLine}>{errors.firstName}</div>}
        </div>
        <div>
          <label htmlFor="lastName" style={label}>
            {copy.fields.lastName}
          </label>
          <input
            id="lastName"
            name="lastName"
            className={fieldClass}
            style={field}
            value={data.lastName}
            onChange={(e) => setData("lastName", e.target.value)}
            autoComplete="family-name"
          />
          {errors.lastName && <div style={errorLine}>{errors.lastName}</div>}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(200px,100%),1fr))", gap: "18px" }}>
        <div>
          <label htmlFor="email" style={label}>
            {copy.fields.email}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            className={fieldClass}
            style={field}
            value={data.email}
            onChange={(e) => setData("email", e.target.value)}
            autoComplete="email"
            required
          />
          {errors.email && <div style={errorLine}>{errors.email}</div>}
        </div>
        <div>
          <label htmlFor="phone" style={label}>
            {copy.fields.phone}
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            className={fieldClass}
            style={field}
            value={data.phone}
            onChange={(e) => setData("phone", e.target.value)}
            autoComplete="tel"
          />
          {errors.phone && <div style={errorLine}>{errors.phone}</div>}
        </div>
      </div>

      <div>
        <label htmlFor="inquiryType" style={label}>
          {copy.fields.topic}
        </label>
        <select
          id="inquiryType"
          name="inquiryType"
          className={fieldClass}
          style={field}
          value={data.inquiryType}
          onChange={(e) => setData("inquiryType", e.target.value)}
        >
          <option value="">{copy.topicPlaceholder}</option>
          {copy.topics.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        {errors.inquiryType && <div style={errorLine}>{errors.inquiryType}</div>}
      </div>

      <div>
        <label htmlFor="message" style={label}>
          {copy.fields.message}
        </label>
        <textarea
          id="message"
          name="message"
          rows={6}
          className={fieldClass}
          style={{ ...field, resize: "vertical" }}
          value={data.message}
          onChange={(e) => setData("message", e.target.value)}
          required
        />
        {errors.message && <div style={errorLine}>{errors.message}</div>}
      </div>

      {/* Honeypot. Real people never see it; bots fill everything. Kept
          identical to the previous form so the controller check still works. */}
      <input
        type="text"
        id="is_robot"
        name="is_robot"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={data.is_robot}
        onChange={(e) => setData("is_robot", e.target.value)}
        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px" }}
      />

      <div>
        <button
          type="submit"
          disabled={processing}
          className="bb-btn bb-btn-gold"
          style={{
            fontFamily: "inherit",
            cursor: processing ? "wait" : "pointer",
            border: 0,
            fontSize: "16px",
            padding: "15px 26px",
            opacity: processing ? 0.7 : 1,
          }}
        >
          {processing ? copy.submitting : copy.submit}
        </button>
        <div style={{ marginTop: "12px", fontSize: "14px", color: color.slate }}>{copy.formNote}</div>
      </div>
    </form>
  );
}
